// @ts-nocheck
import { useState, useRef, RefObject } from "react";
import useIsomorphicLayoutEffect from "@toolkit/Framework/Common/Hooks/useIsomorphicLayoutEffect";

export interface Size {
    width: number;
    height: number;
}

export function useResizeObserver<T extends Element | null>(elementRef: RefObject<T> | null, debounceTime = 100): Size {
    const [size, setSize] = useState<Size>({ width: 0, height: 0 });
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const applySize = (width: number, height: number) => {
        const roundedWidth = Math.round(width || 0);
        const roundedHeight = Math.round(height || 0);
        setSize(prev =>
            prev.width === roundedWidth && prev.height === roundedHeight
                ? prev
                : { width: roundedWidth, height: roundedHeight },
        );
    };

    useIsomorphicLayoutEffect(() => {
        const element = elementRef?.current;
        if (!element) {
            return;
        }

        if (typeof ResizeObserver === "undefined") {
            // Fallback to window resize when ResizeObserver isn't available
            applySize(element.getBoundingClientRect().width, element.getBoundingClientRect().height);
            const onResize = () => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
                timeoutRef.current = setTimeout(() => {
                    applySize(element.getBoundingClientRect().width, element.getBoundingClientRect().height);
                    timeoutRef.current = null;
                }, debounceTime);
            };

            window.addEventListener("resize", onResize, { passive: true });
            return () => {
                window.removeEventListener("resize", onResize);
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
            };
        }

        const observer = new ResizeObserver(entries => {
            const entry = entries[0];
            let width = 0;
            let height = 0;
            if (entry?.borderBoxSize) {
                const box = entry.borderBoxSize[0];
                if (box) {
                    width = box.inlineSize;
                    height = box.blockSize;
                }
            } else if (entry?.contentRect) {
                width = entry.contentRect.width;
                height = entry.contentRect.height;
            } else {
                const rect = element.getBoundingClientRect();
                width = rect.width;
                height = rect.height;
            }

            if (debounceTime > 0) {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
                timeoutRef.current = setTimeout(() => {
                    applySize(width, height);
                    timeoutRef.current = null;
                }, debounceTime);
            } else {
                applySize(width, height);
            }
        });

        observer.observe(element, { box: "border-box" });

        return () => {
            observer.disconnect();
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [elementRef?.current, debounceTime]);

    return size;
}
